import React, { memo, PropsWithChildren, useMemo } from "react";
import {
  FileFragment,
  MessageFragment,
  MessageThreadFragment,
} from "gql/graphql";
import {
  Box,
  Divider,
  HStack,
  PlacementWithLogical,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import colors, { fileThumbFooterGradient } from "app/colors";
import MessageFileReactions from "./message-file-reactions";
import {
  MESSAGE_NOT_SENT_SPINNER_SPEED,
  MESSAGE_STATE,
  REACTION_TYPES,
} from "modules/utils/Constants";
import MessagePopoverReactionButton from "modules/messaging/messages/message-popover/message-popover-reaction-button";
import { VerticalEllipsisIcon } from "modules/icons";
import CircularIconButton from "modules/_shared/buttons/circular-icon-button";
import MessagePopoverDeleteFileButton from "modules/messaging/messages/message-popover/message-popover-delete-file-button";
import MessagePopoverDownloadFileButton from "modules/messaging/messages/message-popover/message-popover-download-file-button";
import MessagePopoverEditImageButton from "modules/messaging/messages/message-popover/message-popover-edit-image-button";
import SyncoTooltip from "modules/_shared/synco-tooltip";
import MessagePopoverForwardFileButton from "modules/messaging/messages/message-popover/message-popover-forward-file-button";
import MessagePopoverReplyFileButton from "modules/messaging/messages/message-popover/message-popover-reply-file-button";
import { useMessageHover } from "modules/providers/message-hover-provider";
import useIsHovering from "modules/hooks/use-is-hovering";

interface IProps extends PropsWithChildren {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  file: FileFragment;
  color?: string;
  messageState: MESSAGE_STATE;
  placement?: PlacementWithLogical;
  offset?: [number, number];
  hover?: "overlay" | "border";
  displayReactionsGradient?: boolean;
}

const MessageFile = ({
  messageThread,
  message,
  file,
  color = colors.white,
  messageState,
  placement = "top-end",
  offset = [-6, -42],
  hover = "overlay",
  displayReactionsGradient = true,
  children,
}: IProps) => {
  const {
    onFileEnter,
    onFileLeave,
    isFileMenuOpen,
    onSubmenuOpen,
    onSubmenuClose,
    isSubMenuOpen,
  } = useMessageHover();

  const { isHovering, onMouseOut, onMouseOver } = useIsHovering();

  const reactions = useMemo(() => {
    if (!message.reactionList) return [];
    return message.reactionList.filter(
      (reaction) => reaction.fileId === file.id,
    );
  }, [file.id, message.reactionList]);

  const _isOpen = useMemo(() => {
    if (messageState === "read-only") return false;
    if (messageState !== "sent") return false;
    return isFileMenuOpen(file.id);
  }, [file.id, isFileMenuOpen, messageState]);

  const _isSubMenuOpen = useMemo(
    () => isSubMenuOpen(file.id, "more"),
    [isSubMenuOpen, file.id],
  );

  const boxBorderColor = useMemo(() => {
    if (isHovering && hover === "border") return colors.grayMain;
    return colors.grayPrimary;
  }, [hover, isHovering]);

  return (
    <Popover
      trigger="hover"
      placement={placement}
      offset={offset}
      closeDelay={200}
      returnFocusOnClose={false}
      isOpen={_isOpen}
      onOpen={onFileEnter(file.id)}
      onClose={onFileLeave(file.id)}
      flip
      isLazy
    >
      <PopoverTrigger>
        <Box
          pos="relative"
          onMouseOver={onMouseOver}
          onMouseOut={onMouseOut}
          borderRadius="8px"
          //border should only be shown when hover === "border" for audio files
          borderWidth={hover === "border" ? 1 : 0}
          borderColor={boxBorderColor}
          w="full"
        >
          {children}
          {hover === "overlay" && (
            <Box
              pos="absolute"
              pointerEvents="none"
              top={0}
              left={0}
              w="full"
              h="full"
              borderRadius="8px"
              bg={colors.black}
              opacity={isHovering ? 0.3 : 0}
              transition="opacity 0.1s ease-in-out"
            />
          )}
          {messageState === "not-sent" && (
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
            >
              <Spinner
                emptyColor={colors.transparent}
                color={colors.blue}
                size="md"
                thickness="4px"
                speed={MESSAGE_NOT_SENT_SPINNER_SPEED}
              />
            </Box>
          )}
          {reactions.length > 0 && (
            <>
              {displayReactionsGradient && (
                <Box
                  position="absolute"
                  bottom={0}
                  bgGradient={fileThumbFooterGradient}
                  w="full"
                  h="47px"
                  borderBottomLeftRadius={4}
                  borderBottomRightRadius={4}
                />
              )}
              <HStack position="absolute" bottom={1.5} left={2} spacing={1}>
                <MessageFileReactions reactions={reactions} color={color} />
              </HStack>
            </>
          )}
        </Box>
      </PopoverTrigger>
      <Portal>
        <PopoverContent w="full" h={9}>
          <HStack spacing={2} h="full" px={2}>
            {REACTION_TYPES.map((reaction) => (
              <MessagePopoverReactionButton
                key={reaction}
                message={message}
                file={file}
                type={reaction}
              />
            ))}
            <Divider
              orientation="vertical"
              h={5}
              borderColor={colors.grayPrimary}
              opacity={1}
              m={1}
            />
            <MessagePopoverReplyFileButton
              messageThread={messageThread}
              message={message}
              file={file}
              draftId={messageThread.id}
            />
            <MessagePopoverForwardFileButton
              message={message}
              messageThread={messageThread}
              file={file}
            />
            <Popover
              placement="top-end"
              trigger="hover"
              offset={[9, 8]}
              isOpen={_isSubMenuOpen}
              onOpen={onSubmenuOpen(file.id, "more")}
              onClose={onSubmenuClose(file.id, "more")}
            >
              <PopoverTrigger>
                <CircularIconButton
                  size="xs"
                  aria-label="More Options"
                  icon={
                    <SyncoTooltip
                      label="More"
                      openDelay={1000}
                      placement="top-end"
                    >
                      <VerticalEllipsisIcon boxSize={5} fill={colors.blue} />
                    </SyncoTooltip>
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                />
              </PopoverTrigger>
              <Portal>
                <PopoverContent w="fit-content" p={2}>
                  <VStack w="full" alignItems="flex-start" spacing={3} p={2}>
                    <MessagePopoverEditImageButton
                      messageThread={messageThread}
                      draftId={messageThread.id}
                      file={file}
                    />
                    <MessagePopoverDownloadFileButton file={file} />
                    <MessagePopoverDeleteFileButton
                      message={message}
                      file={file}
                    />
                  </VStack>
                </PopoverContent>
              </Portal>
            </Popover>
          </HStack>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default memo(MessageFile);
